import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createSeatBlock, createSeatOccupant } from "../../scenes/arrayPrimitives";
import { createTextSprite, updateTextSprite } from "../../scenes/shared/textSpritePrimitives";

const DEFAULT_SEATS = [1, 0, 0, 0, 1, 0, 1];

const EMPTY_IDLE_COLOR = new THREE.Color(0x334155);
const EMPTY_SCANNED_COLOR = new THREE.Color(0x475569);
const OCCUPIED_COLOR = new THREE.Color(0x2563eb);
const ACTIVE_COLOR = new THREE.Color(0xfbbf24);
const CANDIDATE_COLOR = new THREE.Color(0x22c55e);
const BEST_COLOR = new THREE.Color(0xd946ef);

function normalizeSeatsInput(input) {
    return Array.isArray(input) ? input.slice() : DEFAULT_SEATS.slice();
}

function formatSeatLabel(index) {
    return index === null || index === undefined ? "-" : `#${index}`;
}

function formatDistanceLabel(distance) {
    return distance === null || distance === undefined ? "-" : `${distance}`;
}

function getGapTypeLabel(gapType) {
    if (gapType === "leading") {
        return "Leading edge";
    }

    if (gapType === "middle") {
        return "Interior gap";
    }

    if (gapType === "trailing") {
        return "Trailing edge";
    }

    return "-";
}

function isBetterCandidate(distance, seatIndex, bestDistance, bestSeatIndex) {
    if (bestDistance === null || bestSeatIndex === null) {
        return true;
    }

    if (distance > bestDistance) {
        return true;
    }

    if (distance === bestDistance && seatIndex < bestSeatIndex) {
        return true;
    }

    return false;
}

function buildMaxDistanceSteps(seats) {
    const occupiedIndices = [];

    for (let index = 0; index < seats.length; index += 1) {
        if (seats[index] === 1) {
            occupiedIndices.push(index);
        }
    }

    const steps = [
        {
            type: "start",
            seatCount: seats.length,
            occupiedIndices,
        },
    ];

    let previousOccupiedIndex = null;
    let bestDistance = null;
    let bestSeatIndex = null;
    let processedCount = 0;

    for (let index = 0; index < seats.length; index += 1) {
        const value = seats[index];

        steps.push({
            type: "inspect-seat",
            index,
            value,
            processedCount,
        });

        processedCount += 1;

        if (value !== 1) {
            continue;
        }

        if (previousOccupiedIndex === null) {
            if (index > 0) {
                const candidateSeatIndex = 0;
                const candidateDistance = index;
                const isNewBest = isBetterCandidate(
                    candidateDistance,
                    candidateSeatIndex,
                    bestDistance,
                    bestSeatIndex
                );

                if (isNewBest) {
                    bestDistance = candidateDistance;
                    bestSeatIndex = candidateSeatIndex;
                }

                steps.push({
                    type: "evaluate-gap",
                    gapType: "leading",
                    startIndex: 0,
                    endIndex: index - 1,
                    leftOccupiedIndex: null,
                    rightOccupiedIndex: index,
                    candidateSeatIndex,
                    candidateDistance,
                    isNewBest,
                    bestDistance,
                    bestSeatIndex,
                });
            }
        } else if (index - previousOccupiedIndex > 1) {
            const candidateDistance = Math.floor((index - previousOccupiedIndex) / 2);
            const candidateSeatIndex = previousOccupiedIndex + candidateDistance;
            const isNewBest = isBetterCandidate(
                candidateDistance,
                candidateSeatIndex,
                bestDistance,
                bestSeatIndex
            );

            if (isNewBest) {
                bestDistance = candidateDistance;
                bestSeatIndex = candidateSeatIndex;
            }

            steps.push({
                type: "evaluate-gap",
                gapType: "middle",
                startIndex: previousOccupiedIndex + 1,
                endIndex: index - 1,
                leftOccupiedIndex: previousOccupiedIndex,
                rightOccupiedIndex: index,
                candidateSeatIndex,
                candidateDistance,
                isNewBest,
                bestDistance,
                bestSeatIndex,
            });
        }

        previousOccupiedIndex = index;
    }

    if (previousOccupiedIndex !== null && previousOccupiedIndex < seats.length - 1) {
        const candidateSeatIndex = seats.length - 1;
        const candidateDistance = seats.length - 1 - previousOccupiedIndex;
        const isNewBest = isBetterCandidate(
            candidateDistance,
            candidateSeatIndex,
            bestDistance,
            bestSeatIndex
        );

        if (isNewBest) {
            bestDistance = candidateDistance;
            bestSeatIndex = candidateSeatIndex;
        }

        steps.push({
            type: "evaluate-gap",
            gapType: "trailing",
            startIndex: previousOccupiedIndex + 1,
            endIndex: seats.length - 1,
            leftOccupiedIndex: previousOccupiedIndex,
            rightOccupiedIndex: null,
            candidateSeatIndex,
            candidateDistance,
            isNewBest,
            bestDistance,
            bestSeatIndex,
        });
    }

    steps.push({
        type: "complete",
        bestDistance,
        bestSeatIndex,
    });

    return steps;
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1;

    switch (step.type) {
        case "start":
            return {
                id: `max-distance-step-${stepNumber}-start`,
                stepIndex: stepNumber,
                kind: "Setup",
                title: "Initialize seat scan",
                message: `The row contains ${step.seatCount} seats. Occupied seats anchor the leading, interior, and trailing empty gaps that will be evaluated.`,
            };

        case "inspect-seat":
            return {
                id: `max-distance-step-${stepNumber}-inspect-${step.index}`,
                stepIndex: stepNumber,
                kind: "Scan",
                title: `Inspect seat #${step.index}`,
                message:
                    step.value === 1
                        ? `Seat #${step.index} is occupied, so it acts as a gap boundary or anchor for future distance calculations.`
                        : `Seat #${step.index} is empty, so the scan continues until the surrounding occupied anchors determine its closest-neighbor distance.`,
            };

        case "evaluate-gap": {
            const gapRange = `#${step.startIndex}..#${step.endIndex}`;
            const sideMessage =
                step.gapType === "leading"
                    ? `The left edge is open, so the best choice is the first seat in the row.`
                    : step.gapType === "trailing"
                      ? `The right edge is open, so the best choice is the last seat in the row.`
                      : `The gap is bracketed by occupied seats #${step.leftOccupiedIndex} and #${step.rightOccupiedIndex}, so the best choice is the midpoint.`;

            return {
                id: `max-distance-step-${stepNumber}-gap-${step.gapType}-${step.startIndex}-${step.endIndex}`,
                stepIndex: stepNumber,
                kind: "Gap",
                title: `Evaluate ${getGapTypeLabel(step.gapType).toLowerCase()} ${gapRange}`,
                message: `${sideMessage} Gap ${gapRange} proposes seat #${step.candidateSeatIndex} with distance ${step.candidateDistance}. ${step.isNewBest ? `This becomes the new best answer.` : `The current best answer remains seat #${step.bestSeatIndex} with distance ${step.bestDistance}.`}`,
            };
        }

        case "complete":
            return {
                id: `max-distance-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Result",
                title: "Scan complete",
                message: `The farthest valid empty seat is #${step.bestSeatIndex}, whose closest occupied neighbor is ${step.bestDistance} seats away.`,
            };

        default:
            return {
                id: `max-distance-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the maximize distance visualization to the next state.",
            };
    }
}

export default class MaximizeDistanceToClosestPersonScene extends BaseThreeScene {
    setup(props) {
        this.seats = normalizeSeatsInput(props.input);
        const seatCount = this.seats.length;
        const spacing = seatCount > 10 ? 1.15 : 1.35;
        const totalWidth = (seatCount - 1) * spacing;

        this.scene.background = new THREE.Color(0x040816);
        this.scene.fog = new THREE.Fog(0x040816, 10, 24);
        this.camera.position.set(0, 3.4, 10.5 + totalWidth * 0.2);
        this.camera.lookAt(0, 0.6, 0);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.02);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.75);
        keyLight.position.set(4, 7, 6);
        this.scene.add(keyLight);

        const leftLight = new THREE.PointLight(0x60a5fa, 18, 18, 2);
        leftLight.position.set(-4, 4, 4);
        this.scene.add(leftLight);

        const rightLight = new THREE.PointLight(0xd946ef, 18, 18, 2);
        rightLight.position.set(4, 4, 1.5);
        this.scene.add(rightLight);

        this.seatRoot = new THREE.Group();
        this.scene.add(this.seatRoot);

        this.seatMeshes = new Map();
        this.personGroups = new Map();
        this.indexLabels = new Map();
        this.seatPositions = new Map();
        this.presentationTime = 0;

        this.activeSeatIndex = null;
        this.activeSeatValue = null;
        this.currentGap = null;
        this.currentGapType = null;
        this.currentCandidateSeatIndex = null;
        this.currentCandidateDistance = null;
        this.bestSeatIndex = null;
        this.bestDistance = null;
        this.processedIndices = new Set();

        this.floor = new THREE.Mesh(
            new THREE.PlaneGeometry(totalWidth + 4, 5.2),
            new THREE.MeshBasicMaterial({
                color: 0x071226,
                transparent: true,
                opacity: 0.92,
            })
        );
        this.floor.rotation.x = -Math.PI * 0.5;
        this.floor.position.set(0, -0.72, 0.7);
        this.scene.add(this.floor);

        this.gapBand = new THREE.Mesh(
            new THREE.BoxGeometry(1, 0.08, 0.7),
            new THREE.MeshBasicMaterial({
                color: 0x22c55e,
                transparent: true,
                opacity: 0,
            })
        );
        this.gapBand.position.set(0, 0.48, 0);
        this.scene.add(this.gapBand);

        this.activeRing = new THREE.Mesh(
            new THREE.TorusGeometry(0.48, 0.05, 20, 48),
            new THREE.MeshBasicMaterial({
                color: 0xfbbf24,
                transparent: true,
                opacity: 0,
            })
        );
        this.activeRing.rotation.x = Math.PI * 0.5;
        this.scene.add(this.activeRing);

        this.candidateMarker = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 24, 24),
            new THREE.MeshStandardMaterial({
                color: 0x22c55e,
                emissive: 0x22c55e,
                emissiveIntensity: 0.45,
                transparent: true,
                opacity: 0,
            })
        );
        this.scene.add(this.candidateMarker);

        this.bestMarker = new THREE.Mesh(
            new THREE.TorusKnotGeometry(0.2, 0.055, 64, 12),
            new THREE.MeshStandardMaterial({
                color: 0xd946ef,
                emissive: 0xd946ef,
                emissiveIntensity: 0.55,
                transparent: true,
                opacity: 0,
            })
        );
        this.scene.add(this.bestMarker);

        this.candidateBadge = createTextSprite({
            text: "d=-",
            width: 160,
            height: 66,
            fontSize: 24,
            textColor: "#ecfdf5",
            fillStyle: "rgba(21, 128, 61, 0.96)",
            strokeStyle: "rgba(255, 255, 255, 0.12)",
            outlineColor: "rgba(0, 0, 0, 0.95)",
            outlineWidth: 8,
            scale: [0.86, 0.36],
        });
        this.candidateBadge.material.opacity = 0;
        this.scene.add(this.candidateBadge);

        this.bestBadge = createTextSprite({
            text: "best",
            width: 154,
            height: 66,
            fontSize: 24,
            textColor: "#fdf4ff",
            fillStyle: "rgba(162, 28, 175, 0.96)",
            strokeStyle: "rgba(255, 255, 255, 0.12)",
            outlineColor: "rgba(0, 0, 0, 0.95)",
            outlineWidth: 8,
            scale: [0.82, 0.36],
        });
        this.bestBadge.material.opacity = 0;
        this.scene.add(this.bestBadge);

        for (let index = 0; index < seatCount; index += 1) {
            const x = index * spacing - totalWidth * 0.5;
            const position = new THREE.Vector3(x, 0, 0);
            this.seatPositions.set(index, position);

            const seatMesh = createSeatBlock({
                occupied: this.seats[index] === 1,
                emptyColor: EMPTY_IDLE_COLOR.getHex(),
                occupiedColor: OCCUPIED_COLOR.getHex(),
            });
            seatMesh.position.copy(position);
            seatMesh.userData.baseY = position.y;
            this.seatMeshes.set(index, seatMesh);
            this.seatRoot.add(seatMesh);

            const label = createTextSprite({
                text: `#${index}`,
                width: 106,
                height: 54,
                fontSize: 20,
                textColor: "#dbeafe",
                fillStyle: "rgba(15, 23, 42, 0.9)",
                strokeStyle: "rgba(255, 255, 255, 0.1)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: [0.48, 0.24],
            });
            label.position.set(x, -0.62, 0);
            this.indexLabels.set(index, label);
            this.seatRoot.add(label);

            if (this.seats[index] === 1) {
                const personGroup = createSeatOccupant();

                personGroup.position.copy(position);
                personGroup.userData.baseY = position.y;
                this.personGroups.set(index, personGroup);
                this.seatRoot.add(personGroup);
            }
        }

        this.resetAnimationState();
        this.advanceStep();
    }

    resetAnimationState() {
        this.steps = buildMaxDistanceSteps(this.seats);
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;
        this.presentationTime = 0;

        this.activeSeatIndex = null;
        this.activeSeatValue = null;
        this.currentGap = null;
        this.currentGapType = null;
        this.currentCandidateSeatIndex = null;
        this.currentCandidateDistance = null;
        this.bestSeatIndex = null;
        this.bestDistance = null;
        this.processedIndices = new Set();

        this.resetStepLog([]);
        this.replaceUiState({
            seatCount: this.seats.length,
            processedCount: 0,
            activeSeatLabel: "-",
            activeSeatValue: "-",
            currentGapLabel: "-",
            candidateSeatLabel: "-",
            candidateDistanceLabel: "-",
            bestSeatLabel: "-",
            bestDistanceLabel: "-",
        });
        this.syncPlaybackState();
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    syncUiState() {
        this.replaceUiState({
            seatCount: this.seats.length,
            processedCount: this.processedIndices.size,
            activeSeatLabel: formatSeatLabel(this.activeSeatIndex),
            activeSeatValue:
                this.activeSeatValue === null || this.activeSeatValue === undefined
                    ? "-"
                    : `${this.activeSeatValue}`,
            currentGapLabel: getGapTypeLabel(this.currentGapType),
            candidateSeatLabel: formatSeatLabel(this.currentCandidateSeatIndex),
            candidateDistanceLabel: formatDistanceLabel(this.currentCandidateDistance),
            bestSeatLabel: formatSeatLabel(this.bestSeatIndex),
            bestDistanceLabel: formatDistanceLabel(this.bestDistance),
        });
    }

    getStepDuration(step) {
        switch (step.type) {
            case "start":
                return 1.15;
            case "inspect-seat":
                return 0.65;
            case "evaluate-gap":
                return step.isNewBest ? 1.25 : 0.95;
            case "complete":
                return 2.2;
            default:
                return 0.8;
        }
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex));
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        switch (step.type) {
            case "start": {
                this.activeSeatIndex = null;
                this.activeSeatValue = null;
                this.currentGap = null;
                this.currentGapType = null;
                this.currentCandidateSeatIndex = null;
                this.currentCandidateDistance = null;
                break;
            }

            case "inspect-seat": {
                this.activeSeatIndex = step.index;
                this.activeSeatValue = step.value;
                this.currentGap = null;
                this.currentGapType = null;
                this.currentCandidateSeatIndex = null;
                this.currentCandidateDistance = null;
                this.processedIndices.add(step.index);
                break;
            }

            case "evaluate-gap": {
                this.activeSeatIndex = step.candidateSeatIndex;
                this.activeSeatValue = 0;
                this.currentGap = {
                    startIndex: step.startIndex,
                    endIndex: step.endIndex,
                };
                this.currentGapType = step.gapType;
                this.currentCandidateSeatIndex = step.candidateSeatIndex;
                this.currentCandidateDistance = step.candidateDistance;
                this.bestSeatIndex = step.bestSeatIndex;
                this.bestDistance = step.bestDistance;
                break;
            }

            case "complete": {
                this.activeSeatIndex = null;
                this.activeSeatValue = null;
                this.currentGap = null;
                this.currentGapType = null;
                this.currentCandidateSeatIndex = null;
                this.currentCandidateDistance = null;
                this.bestSeatIndex = step.bestSeatIndex;
                this.bestDistance = step.bestDistance;
                break;
            }

            default:
                break;
        }
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    update(delta) {
        if (!this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();
            if (!advanced) {
                this.pause();
            }
        }

        this.updateSeats(this.presentationTime);
        this.updateGapBand();
        this.updateMarkers(this.presentationTime);
    }

    updateSeats(elapsed) {
        this.seatMeshes.forEach((seatMesh, index) => {
            const seatValue = this.seats[index];
            const isProcessed = this.processedIndices.has(index);
            const isActive = this.activeSeatIndex === index;
            const isCandidate = this.currentCandidateSeatIndex === index;
            const isBest = this.bestSeatIndex === index;

            let targetColor = seatValue === 1 ? OCCUPIED_COLOR : EMPTY_IDLE_COLOR;
            let targetScale = 1;
            let targetEmissiveIntensity = seatValue === 1 ? 0.26 : 0.12;

            if (seatValue === 0 && isProcessed) {
                targetColor = EMPTY_SCANNED_COLOR;
                targetEmissiveIntensity = 0.16;
            }

            if (isBest) {
                targetColor = BEST_COLOR;
                targetScale = 1.08;
                targetEmissiveIntensity = 0.54;
            }

            if (isCandidate) {
                targetColor = CANDIDATE_COLOR;
                targetScale = 1.1;
                targetEmissiveIntensity = 0.62;
            }

            if (isActive) {
                targetColor = ACTIVE_COLOR;
                targetScale = 1.15 + Math.sin(elapsed * 5.2) * 0.03;
                targetEmissiveIntensity = 0.9;
            }

            seatMesh.material.color.lerp(targetColor, 0.16);
            seatMesh.material.emissive.lerp(targetColor, 0.16);
            seatMesh.material.emissiveIntensity = THREE.MathUtils.lerp(
                seatMesh.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            const nextScale = THREE.MathUtils.lerp(seatMesh.scale.x, targetScale, 0.16);
            seatMesh.scale.set(nextScale, THREE.MathUtils.lerp(seatMesh.scale.y, targetScale, 0.16), nextScale);
            seatMesh.position.y = THREE.MathUtils.lerp(
                seatMesh.position.y,
                seatMesh.userData.baseY + Math.sin(elapsed * 1.8 + index * 0.3) * 0.02,
                0.12
            );

            const label = this.indexLabels.get(index);
            label.position.y = THREE.MathUtils.lerp(label.position.y, -0.62, 0.18);

            if (isBest || isCandidate || isActive) {
                updateTextSprite(label, `#${index}`, {
                    fontSize: 20,
                    textColor: "#ffffff",
                    fillStyle: isActive
                        ? "rgba(146, 64, 14, 0.96)"
                        : isCandidate
                          ? "rgba(21, 128, 61, 0.96)"
                          : "rgba(162, 28, 175, 0.96)",
                    strokeStyle: "rgba(255, 255, 255, 0.12)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 6,
                });
            } else {
                updateTextSprite(label, `#${index}`, {
                    fontSize: 20,
                    textColor: "#dbeafe",
                    fillStyle: "rgba(15, 23, 42, 0.9)",
                    strokeStyle: "rgba(255, 255, 255, 0.1)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 6,
                });
            }
        });

        this.personGroups.forEach((group, index) => {
            group.position.y = THREE.MathUtils.lerp(
                group.position.y,
                group.userData.baseY + Math.sin(elapsed * 1.6 + index * 0.45) * 0.04,
                0.1
            );
        });
    }

    updateGapBand() {
        if (!this.currentGap) {
            this.gapBand.material.opacity = THREE.MathUtils.lerp(
                this.gapBand.material.opacity,
                0,
                0.16
            );
            return;
        }

        const startPosition = this.seatPositions.get(this.currentGap.startIndex);
        const endPosition = this.seatPositions.get(this.currentGap.endIndex);

        if (!startPosition || !endPosition) {
            return;
        }

        const centerX = (startPosition.x + endPosition.x) * 0.5;
        const width = Math.abs(endPosition.x - startPosition.x) + 0.92;
        this.gapBand.position.x = THREE.MathUtils.lerp(this.gapBand.position.x, centerX, 0.16);
        this.gapBand.scale.x = THREE.MathUtils.lerp(this.gapBand.scale.x, width, 0.16);
        this.gapBand.material.opacity = THREE.MathUtils.lerp(
            this.gapBand.material.opacity,
            0.38,
            0.16
        );
        this.gapBand.material.color.lerp(
            this.currentGapType === "middle" ? new THREE.Color(0x22c55e) : new THREE.Color(0x38bdf8),
            0.16
        );
    }

    updateMarkers(elapsed) {
        if (this.activeSeatIndex === null) {
            this.activeRing.material.opacity = THREE.MathUtils.lerp(
                this.activeRing.material.opacity,
                0,
                0.18
            );
        } else {
            const activePosition = this.seatPositions.get(this.activeSeatIndex);
            this.activeRing.position.x = THREE.MathUtils.lerp(
                this.activeRing.position.x,
                activePosition.x,
                0.18
            );
            this.activeRing.position.y = 0.22;
            this.activeRing.scale.setScalar(1 + Math.sin(elapsed * 5.2) * 0.05);
            this.activeRing.material.opacity = THREE.MathUtils.lerp(
                this.activeRing.material.opacity,
                0.92,
                0.18
            );
        }

        if (this.currentCandidateSeatIndex === null) {
            this.candidateMarker.material.opacity = THREE.MathUtils.lerp(
                this.candidateMarker.material.opacity,
                0,
                0.16
            );
            this.candidateBadge.material.opacity = THREE.MathUtils.lerp(
                this.candidateBadge.material.opacity,
                0,
                0.16
            );
        } else {
            const candidatePosition = this.seatPositions.get(this.currentCandidateSeatIndex);
            this.candidateMarker.position.x = THREE.MathUtils.lerp(
                this.candidateMarker.position.x,
                candidatePosition.x,
                0.18
            );
            this.candidateMarker.position.y = 1.18 + Math.sin(elapsed * 5.6) * 0.08;
            this.candidateMarker.position.z = 0;
            this.candidateMarker.material.opacity = THREE.MathUtils.lerp(
                this.candidateMarker.material.opacity,
                0.96,
                0.16
            );

            updateTextSprite(this.candidateBadge, `d=${this.currentCandidateDistance}`, {
                fontSize: 24,
                textColor: "#ecfdf5",
                fillStyle: "rgba(21, 128, 61, 0.96)",
                strokeStyle: "rgba(255, 255, 255, 0.12)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 8,
            });
            this.candidateBadge.position.x = THREE.MathUtils.lerp(
                this.candidateBadge.position.x,
                candidatePosition.x,
                0.18
            );
            this.candidateBadge.position.y = 1.74;
            this.candidateBadge.position.z = 0;
            this.candidateBadge.material.opacity = THREE.MathUtils.lerp(
                this.candidateBadge.material.opacity,
                1,
                0.16
            );
        }

        if (this.bestSeatIndex === null) {
            this.bestMarker.material.opacity = THREE.MathUtils.lerp(
                this.bestMarker.material.opacity,
                0,
                0.16
            );
            this.bestBadge.material.opacity = THREE.MathUtils.lerp(
                this.bestBadge.material.opacity,
                0,
                0.16
            );
        } else {
            const bestPosition = this.seatPositions.get(this.bestSeatIndex);
            this.bestMarker.position.x = THREE.MathUtils.lerp(
                this.bestMarker.position.x,
                bestPosition.x,
                0.16
            );
            this.bestMarker.position.y = 1.55 + Math.sin(elapsed * 3.6) * 0.06;
            this.bestMarker.position.z = 0;
            this.bestMarker.rotation.x += 0.02;
            this.bestMarker.rotation.y += 0.035;
            this.bestMarker.material.opacity = THREE.MathUtils.lerp(
                this.bestMarker.material.opacity,
                0.9,
                0.16
            );

            updateTextSprite(this.bestBadge, `best=${this.bestDistance}`, {
                fontSize: 24,
                textColor: "#fdf4ff",
                fillStyle: "rgba(162, 28, 175, 0.96)",
                strokeStyle: "rgba(255, 255, 255, 0.12)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 8,
            });
            this.bestBadge.position.x = THREE.MathUtils.lerp(
                this.bestBadge.position.x,
                bestPosition.x,
                0.16
            );
            this.bestBadge.position.y = 2.22;
            this.bestBadge.position.z = 0;
            this.bestBadge.material.opacity = THREE.MathUtils.lerp(
                this.bestBadge.material.opacity,
                1,
                0.16
            );
        }
    }

    onDispose() {
        const disposeSpriteTexture = (sprite) => {
            if (!sprite || !sprite.material || !sprite.material.map) {
                return;
            }

            sprite.material.map.dispose();
        };

        this.indexLabels.forEach(disposeSpriteTexture);
        disposeSpriteTexture(this.candidateBadge);
        disposeSpriteTexture(this.bestBadge);
    }
}
